import "@shoelace-style/shoelace/dist/components/avatar/avatar.js";

import { html } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";
import { litIfUserInGroup } from "../LitUtils";
import { makeCustomEvent } from "../../Utils";
import { watch } from "../internal/watch";

import "../DatabaseOperations";
import "./SettingsCard";
import "./UserSettings";
import "../HomeNavigateControl/HomeNavigateControl";

import { styles } from "./SettingsControl.style";
import { Kernel } from "../../Kernel";

@customElement("ds-settings-control")
export class SettingsControl extends BaseElement {
    static override styles = [styles];

    @property({ type: String }) view: string | undefined;
    @state() protected _showGPXExport = false;
    @state() protected _amplifyLogLevel = "ERROR";

    private _getTools() {
        const all = [
            {
                signedInOnly: false,
                text: "Go to location",
                icon: "send",
                eventName: "ds-input-coords",
            },
            {
                signedInOnly: true,
                text: "Create a new place",
                icon: "plus-circle",
                eventName: "ds-add-new-place",
            },
            {
                signedInOnly: true,
                text: "Import media",
                icon: "cassette",
                eventName: "ds-import-media",
            },
            {
                signedInOnly: false,
                text: "Export to GPX",
                icon: "share",
                eventName: "ds-export-gpx",
            },
            {
                signedInOnly: false,
                text: "Going offline",
                icon: "globe-americas",
                eventName: "ds-offline-maps",
            },
            {
                hidden: !Kernel.instance().user?.inGroup("editors"),
                text: "Moderation queue",
                icon: "save2",
                eventName: "ds-show-moderation-queue",
            },
            {
                signedInOnly: true,
                text: "Media uploads",
                icon: "globe-americas",
                eventName: "ds-show-media-uploads",
            },
            {
                text: "Changelog",
                icon: "clock-history",
                eventName: "ds-show-changelog",
            },
            {
                text: "Release Notes",
                icon: "list-ul",
                eventName: "ds-show-release-notes",
            },
            {
                text: "Contributions",
                icon: "stars",
                eventName: "ds-show-contributions",
            },
        ];

        return all.filter((t) => !("hidden" in t && t["hidden"] === true));
    }

    public constructor() {
        super();
    }

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._amplifyLogLevel = this._kernel.amplifyLoggingLevel.get();
    }

    @watch("_amplifyLogLevel")
    public amplifyLogLevelChanged(oldValue: any, newValue: any) {
        if (oldValue !== newValue) {
            this._kernel.amplifyLoggingLevel.set(newValue as string);
        }
    }

    override render() {
        return html` <div class="settings-container flex-column">
            <ds-settings-card label="Tools" iconName="wrench-adjustable">
                <div class="settings-tools flex-column">
                    <sl-button
                        ><sl-icon name="filetype-doc"></sl-icon
                        ><a href="https://www.cavedb.net/app-help/" target="_blank"
                            >Documentation ></a
                        >
                    </sl-button>

                    ${this._getTools().map(
                        (x) =>
                            html`<sl-button
                                ?disabled=${x.signedInOnly ? !this._kernel.hasUser() : false}
                                class="${x.icon}"
                                @click=${() => this._raiseEvent(x.eventName)}
                                ><sl-icon name="${x.icon}"></sl-icon>${x.text} &gt;
                            </sl-button>`
                    )}
                </div>
            </ds-settings-card>

            <ds-settings-card label="User settings" iconName="person">
                <ds-user-settings-control></ds-user-settings-control>
            </ds-settings-card>

            <ds-settings-card label="Car position" iconName="car-front">
                <ds-home-navigate-control></ds-home-navigate-control>
            </ds-settings-card>

            <!-- ds-settings-card label="Views" iconName="window-sidebar" onlyAuthenticated>
                <br />
                <sl-checkbox
                    ?checked=${this.view === "data-table"}
                    @sl-change=${(e: any) => {
                this.view = e.target.checked ? "data-table" : "map";
                this.dispatchEvent(makeCustomEvent("ds-change-view", { view: this.view }));
            }}
                    >Spreadsheet view</sl-checkbox
                >
            </ds-settings-card -->
            ${litIfUserInGroup(
                "developer",
                () => html`
                    <ds-settings-card label="Database" iconName="database-fill">
                        <br />
                        <ds-database-operations></ds-database-operations>
                        <sl-select
                            label="Amplify logging level (restart required)"
                            style="font-size: 1.5em;"
                            .value=${this._amplifyLogLevel}
                            @sl-change=${(e: any) => {
                                this._amplifyLogLevel = e.target.value;
                            }}
                        >
                            <sl-option value="VERBOSE">Verbose</sl-option>
                            <sl-option value="DEBUG">Debug</sl-option>
                            <sl-option value="INFO">Info</sl-option>
                            <sl-option value="WARN">Warning</sl-option>
                            <sl-option value="ERROR">Error</sl-option>
                        </sl-select>
                    </ds-settings-card>
                `
            )}
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-settings-control": SettingsControl;
    }
}
